import { extend, useThree } from '@react-three/fiber';
import React, { useEffect, useRef } from 'react';
import { FrontSide, MeshBasicMaterial, Vector3 } from 'three';
import { Text } from 'troika-three-text';
import { createDerivedMaterial } from 'troika-three-utils';
import { useExportContext } from '../../provider/ExportProvider';
import { DEFAULT_ZOOM_VALUE, ROBOTO_FONT_PATH } from '../../utils/constants';
import RENDER_ORDERS from '../../utils/renderOrder';

extend({ Text });

interface CustomTextProps {
  text: string;
  fontSize?: number;
  color?: string | number;
  position?: Vector3;
  rotation?: number;
  staggeredZoomData: number;
}

const CustomTextComponent: React.FC<CustomTextProps> = ({
  text,
  fontSize = 0.6,
  color = '0x000000',
  position = new Vector3(0, 0, 0),
  rotation,
  staggeredZoomData,
}) => {
  const textRef = useRef<Text>(null);
  const { invalidate } = useThree();
  const { isExporting, exportCameraWidth, exportCameraHeight } = useExportContext();

  const customMaterial = useRef(
    createDerivedMaterial(
      new MeshBasicMaterial({
        color: 'black',
        side: FrontSide,
        transparent: true,
      }),
      {
        vertexDefs: `
          uniform float cameraZoom;
          uniform float defaultZoomValue;
          uniform bool isExporting;
          uniform float exportWidth;
          uniform float exportHeight;
        `,
        vertexTransform: `
          vec3 scaledPosition = position;
          if (isExporting) {
            float baseSize = 50.0;  
            float minSizeThreshold = 80.0;  
            float midSizeThreshold = 150.0; 
            float bigSizeThreshold = 300.0; 
            float hugeSizeThreshold = 450.0; 
            float smallScale = 0.8;   
            float midScale = 1.5;     
            float bigScale = 2.0;     
            float hugeScale = 2.7;    
            float maxScale = 3.0;     
            float effectiveHeight = max(exportHeight, minSizeThreshold);
            float effectiveWidth = max(exportWidth, minSizeThreshold);
            float largestDimension = max(effectiveHeight, effectiveWidth);
            float scalingFactor = baseSize / largestDimension;
            scalingFactor = log2(1.0 + scalingFactor);
            float lowClamp = 0.2;
            float highClamp = 5.0;
            scalingFactor = clamp(scalingFactor, lowClamp, highClamp);
            if (largestDimension > hugeSizeThreshold) {
                scalingFactor = max(scalingFactor, maxScale);
            } else if (largestDimension > bigSizeThreshold) {
                scalingFactor = max(scalingFactor, hugeScale);
            } else if (largestDimension > midSizeThreshold) {
                scalingFactor = max(scalingFactor, bigScale);
            } else if (largestDimension > minSizeThreshold) {
                scalingFactor = max(scalingFactor, midScale);
            } else {
                scalingFactor = max(scalingFactor, smallScale);
            }
            scaledPosition *= scalingFactor;
            position = scaledPosition;            
          } else {
            float baseSize = defaultZoomValue;
            float scaleFactor = baseSize / cameraZoom;
            float high = 3.0;
            float low = 0.4;
            float limitedScaleFactor = clamp(scaleFactor, low, high);
            scaledPosition *= limitedScaleFactor;
            position = scaledPosition;
          }
        `,
        uniforms: {
          cameraZoom: { value: staggeredZoomData || 1.0 },
          defaultZoomValue: { value: DEFAULT_ZOOM_VALUE },
          isExporting: { value: false },
          exportWidth: { value: 1.0 },
          exportHeight: { value: 1.0 },
        },
      },
    ),
  ).current;

  useEffect(() => {
    customMaterial.uniforms.cameraZoom.value = staggeredZoomData || 1.0;
    customMaterial.uniforms.isExporting.value = isExporting;
    customMaterial.uniforms.exportWidth.value = exportCameraWidth;
    customMaterial.uniforms.exportHeight.value = exportCameraHeight;
  }, [staggeredZoomData, isExporting, exportCameraWidth, exportCameraHeight, customMaterial]);

  useEffect(() => {
    if (textRef.current) {
      textRef.current.text = text; 
      textRef.current.sync(() => {
        invalidate(); 
      });
    }
  }, [text, invalidate]);

  return (
    <group name="customTextGroup" position={position} renderOrder={RENDER_ORDERS.texts} rotation={[0, 0, rotation ?? 0]}>
      <text
        //@ts-expect-error textRef
        ref={textRef}
        text={text}
        fontSize={fontSize}
        font={ROBOTO_FONT_PATH}
        color={color as string}
        anchorX="center"
        anchorY="middle"
        outline
        material={customMaterial}
      />
    </group>
  );
};

export default CustomTextComponent;
